import { useTranslation } from 'react-i18next';
import { Chip, Modal } from '@/components/ui';
import type { BulkLevelAssignVM } from './useBulkLevelAssign';
import type { ScopedAccountsVM } from './useScopedAccounts';

/** Frame 35's "Áp cho:" chips — the scope and filters this run is bound to. */
function ScopeChips({ vm }: { vm: ScopedAccountsVM }) {
    const { t } = useTranslation();
    const chips: string[] = [];
    const scopeLabel =
        vm.orgName ?? t('console.hierarchy.scopedAccounts.bulkAssign.scopeFallback');
    chips.push(
        vm.filters.includeChildren
            ? `${scopeLabel}${t('console.hierarchy.scopedAccounts.bulkAssign.includeChildrenSuffix')}`
            : scopeLabel
    );
    if (vm.filters.childOrgId) {
        chips.push(
            t('console.hierarchy.scopedAccounts.bulkAssign.chipOrgLabel', {
                name:
                    vm.orgNameById.get(vm.filters.childOrgId) ??
                    t('console.hierarchy.scopedAccounts.bulkAssign.childOrgFallback'),
            })
        );
    }
    if (vm.filters.levelId) {
        const level = vm.activeLevels.find((item) => item.id === vm.filters.levelId);
        chips.push(
            t('console.hierarchy.scopedAccounts.bulkAssign.chipLevelLabel', {
                name: level?.name ?? '—',
            })
        );
    }
    if (vm.filters.issue) {
        chips.push(
            vm.filters.issue === 'no_level'
                ? t('console.hierarchy.scopedAccounts.bulkAssign.issueNoLevel')
                : t('console.hierarchy.scopedAccounts.bulkAssign.issueStaleRoleMap')
        );
    }
    if (vm.filters.isActive !== null) {
        chips.push(
            vm.filters.isActive
                ? t('console.hierarchy.scopedAccounts.bulkAssign.statusActive')
                : t('console.hierarchy.scopedAccounts.bulkAssign.statusInactive')
        );
    }
    return (
        <div className="sa-dialog-scope">
            <span className="hz-card-note">
                {t('console.hierarchy.scopedAccounts.bulkAssign.appliesToLabel')}
            </span>
            <span className="hz-chip-row">
                {chips.map((label) => (
                    <span key={label} className="portal-console-chip">
                        {label}
                    </span>
                ))}
            </span>
        </div>
    );
}

/**
 * EAA-1839 (frame 35): the bulk Level-assign dialog — target level, the three-way
 * dry-run preview, the replace opt-in, and the per-application impact.
 *
 * THE OPT-IN CHECKBOX IS A CLIENT-SIDE ID FILTER, not a request flag, and that is
 * forced by the contract: `bulk-apply`'s body is `{user_subs, level_id}` and
 * `AssignmentService.bulk_apply` re-derives the buckets itself and writes BOTH
 * `new` and `changed`. So leaving the box unticked means the `changed` subs are
 * simply not in the request — see `useBulkLevelAssign` for the exact list. The
 * count in the footer button is that narrowed list's length, never the selection
 * size, so the button can never claim to touch more rows than it sends.
 *
 * `valid_level: false` is surfaced rather than swallowed. It means the chosen level
 * does not belong to this đơn vị (or is inactive); every sub then comes back in
 * `skipped` and nothing can be applied. The picker only offers this org's active
 * levels, so it should not happen — but a level deactivated in another tab would
 * otherwise look like a silent zero-effect run.
 */
export function BulkLevelAssignDialog({
    vm,
    bulk,
}: {
    vm: ScopedAccountsVM;
    bulk: BulkLevelAssignVM;
}) {
    const { t } = useTranslation();
    const targetCount = vm.bulkUserSubs.length;
    const preview = bulk.preview;
    const invalidLevel = Boolean(preview && !preview.valid_level);

    return (
        <Modal
            open={bulk.open}
            onCancel={bulk.close}
            title={t('console.hierarchy.scopedAccounts.bulkAssign.title', {
                value: targetCount.toLocaleString('vi-VN'),
            })}
            footer={
                <>
                    <button
                        type="button"
                        className="portal-btn"
                        onClick={bulk.close}
                        disabled={bulk.applyBusy}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-primary"
                        onClick={bulk.runApply}
                        disabled={bulk.applyBusy || bulk.applySubs.length === 0}
                    >
                        {bulk.applyBusy
                            ? t('console.hierarchy.scopedAccounts.bulkAssign.applyBusy')
                            : t('console.hierarchy.scopedAccounts.bulkAssign.applyButton', {
                                  value: bulk.applySubs.length.toLocaleString('vi-VN'),
                              })}
                    </button>
                </>
            }
        >
            <p className="hz-card-note">
                {t('console.hierarchy.scopedAccounts.bulkAssign.filterCountNote')}
            </p>

            <ScopeChips vm={vm} />

            <label className="sa-dialog-field">
                {t('console.hierarchy.scopedAccounts.bulkAssign.levelFieldLabel')}
                <select
                    value={bulk.levelId}
                    onChange={(event) => bulk.setLevelId(event.target.value)}
                    aria-label={t(
                        'console.hierarchy.scopedAccounts.bulkAssign.levelSelectAriaLabel'
                    )}
                >
                    <option value="">
                        {t('console.hierarchy.scopedAccounts.bulkAssign.levelPlaceholderOption')}
                    </option>
                    {vm.activeLevels.map((level) => (
                        <option key={level.id} value={level.id}>
                            {level.name} ({level.role_key})
                        </option>
                    ))}
                </select>
            </label>

            {!bulk.levelId && (
                <p className="hz-card-note">
                    {t('console.hierarchy.scopedAccounts.bulkAssign.selectLevelHint')}
                </p>
            )}

            {bulk.previewQuery.isLoading && (
                <p className="hz-card-note" role="status" aria-live="polite">
                    {t('console.hierarchy.scopedAccounts.bulkAssign.previewLoading')}
                </p>
            )}

            {bulk.previewQuery.isError && (
                <p className="portal-admin-error">
                    {t('console.hierarchy.scopedAccounts.bulkAssign.previewError')}
                </p>
            )}

            {invalidLevel && (
                <p className="portal-admin-error">
                    {t('console.hierarchy.scopedAccounts.bulkAssign.invalidLevelError')}
                </p>
            )}

            {preview && preview.valid_level && (
                <>
                    <div className="hz-deactivate-note sa-preview">
                        <span className="hz-card-title">
                            {t('console.hierarchy.scopedAccounts.bulkAssign.previewTitle')}
                        </span>
                        <div className="hz-level-stat-row">
                            <span
                                className="sa-preview-dot sa-preview-dot--new"
                                aria-hidden="true"
                            />
                            <span>{t('console.hierarchy.scopedAccounts.bulkAssign.previewNew')}</span>
                            <span className="hz-level-stat-num">{preview.new.length}</span>
                        </div>
                        <div className="hz-level-stat-row">
                            <span
                                className="sa-preview-dot sa-preview-dot--changed"
                                aria-hidden="true"
                            />
                            <span>
                                {t('console.hierarchy.scopedAccounts.bulkAssign.previewChanged')}
                            </span>
                            <span className="hz-level-stat-num">{bulk.changedCount}</span>
                        </div>
                        <div className="hz-level-stat-row">
                            <span
                                className="sa-preview-dot sa-preview-dot--skip"
                                aria-hidden="true"
                            />
                            <span>
                                {t('console.hierarchy.scopedAccounts.bulkAssign.previewSkipped')}
                            </span>
                            <span className="hz-level-stat-num">{preview.skipped.length}</span>
                        </div>
                    </div>

                    {bulk.changedCount > 0 && (
                        <label className="sa-optin">
                            <input
                                type="checkbox"
                                className="rm-check"
                                checked={bulk.replaceChanged}
                                onChange={(event) => bulk.setReplaceChanged(event.target.checked)}
                            />
                            <span>
                                {t('console.hierarchy.scopedAccounts.bulkAssign.replaceOptInLabel', {
                                    count: bulk.changedCount,
                                })}
                            </span>
                        </label>
                    )}

                    <div className="hz-deactivate-note">
                        <span className="hz-card-title">
                            {t('console.hierarchy.scopedAccounts.bulkAssign.impactTitle')}
                        </span>
                        {bulk.impactRows.length === 0 && (
                            <p className="hz-card-unavailable">
                                {t('console.hierarchy.scopedAccounts.bulkAssign.impactEmpty')}
                            </p>
                        )}
                        {/* Deduped per application — the endpoint returns one entry per
                            app-scope row, so an org with 4 ACH tenants sends `ach` back
                            4 times. See `useBulkLevelAssign.impactRows`. */}
                        {bulk.impactRows.map((row) => (
                            <div key={row.application_id} className="hz-level-stat-row">
                                <Chip>{row.application_code}</Chip>
                                <span className="hz-chip-row sa-impact-roles">
                                    {row.paused && (
                                        <span className="portal-console-chip is-paused">
                                            {t(
                                                'console.hierarchy.scopedAccounts.bulkAssign.impactPaused'
                                            )}
                                        </span>
                                    )}
                                    {!row.paused && row.app_role_keys.length === 0 && (
                                        <span className="hz-card-unavailable">
                                            {t(
                                                'console.hierarchy.scopedAccounts.bulkAssign.impactNoMapping'
                                            )}
                                        </span>
                                    )}
                                    {!row.paused &&
                                        row.app_role_keys.map((key) => (
                                            <Chip key={key}>{key}</Chip>
                                        ))}
                                </span>
                            </div>
                        ))}
                        {/* The preview carries `application_code` only — see
                            `BulkLevelImpactRow`: the names live behind GET /admin/apps,
                            which is super-admin-only and would 403 for an org-admin. */}
                        <p className="hz-card-unavailable">
                            {t('console.hierarchy.scopedAccounts.bulkAssign.impactCodeOnlyNote')}
                        </p>
                    </div>

                    {/* Frame 35 ends with "Trên 200 dòng sẽ chạy nền — theo dõi tiến độ và kết
                        quả từng dòng ở màn việc chạy nền." There is no background-job surface
                        in this app and `bulk-apply` is fully synchronous
                        (`AssignmentService.bulk_apply` loops the writes inline and returns the
                        finished result), so that sentence would promise a screen that does not
                        exist. `synchronousNote` states what actually happens instead. */}
                    <p className="hz-card-note">
                        {t('console.hierarchy.scopedAccounts.bulkAssign.synchronousNote')}
                    </p>
                </>
            )}
        </Modal>
    );
}
